import React, { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Box from "@amzn/meridian/esm/components/box";
import { Divider, Icon, SideMenu, SideMenuLink, SideMenuSpacer } from "@amzn/meridian";
import calendarTokens from "@amzn/meridian-tokens/base/icon/calendar";
import SidebarInClient from "../common/SidebarInClient";
import { CLIENT_TAB, CLIENT_TAB_LABEL } from "../inMainClient.constants";
import SummaryFeedback from "../../../../components/feedback/SummaryFeedback";
import SummaryListButton from "../../../../components/summary/SummaryListButton";
import SummaryBodyInDetailPage from "../../../../components/summary/SummaryBodyInDetailPage";
import SummaryNotApprovedFeedback from "../../../../components/feedback/SummaryNotApprovedFeedback";
import { SUMMARY_FEEDBACK_STATUS, SUMMARY_TYPES } from "../../../../constants";
import { VERBOSITY_LEVELS } from "../../../../constants";
import AttendeeListInDetailPage from "../../../../components/summary/AttendeeListInDetailPage";
import MeetingParticipants from "../../../../components/summary/MeetingParticipants";
import badgeModerate from "../../../../images/badge_moderate.png";
import badgeDetailed from "../../../../images/badge_detailed.png";

const SummaryDetailInClient = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(CLIENT_TAB.SUMMARIES);
  const [showParticipants, setShowParticipants] = useState(false);
  const fromPage = location.state?.fromPage || 1;

  const summary = location.state?.summary;
  const isAttendanceReportEnabled = location.state?.isAttendanceReportEnabled;
  const isVerboseSummariesEnabled = location.state?.isVerboseSummariesEnabled;
  const summaryFeedbackStatus = SUMMARY_TYPES.APPROVED.includes(summary?.summaryType) 
    ? SUMMARY_FEEDBACK_STATUS.APPROVED
    : SUMMARY_TYPES.NOT_APPROVED.includes(summary?.summaryType)
    ? SUMMARY_FEEDBACK_STATUS.NOT_APPROVED
    : SUMMARY_FEEDBACK_STATUS.OTHER;

  if (!summary || summary.aimsUuid !== id) {
    return (
      <Row spacing="none" spacingInset="none">
        <SideMenu>
          <SideMenuLink selected>{CLIENT_TAB_LABEL.SUMMARIES}</SideMenuLink>
          <SideMenuLink>{CLIENT_TAB_LABEL.FEEDBACK}</SideMenuLink>
          {[...Array(10)].map((_, idx) => (
            <SideMenuSpacer key={idx} />
          ))}
        </SideMenu>
        <Column spacingInset="400">
          <Text type="b100" color="error">
            Summary not found.
          </Text>
        </Column>
      </Row>
    );
  }

  const onSummaryListInClient = () => {
    navigate("/client", {
      state: {
        fromDetail: true,
        returnPage: fromPage,
        selectedTab: selectedTab,
      },
    });
  };

  return (
    <Row spacing="500" spacingInset="none" alignmentVertical="top" widths={["fit", "fill"]}>
      <SidebarInClient
        selectedTab={selectedTab}
        isVerboseSummariesEnabled={isVerboseSummariesEnabled}
        setSelectedTab={(tab) => {
          setSelectedTab(tab);
          navigate("/client", {
            state: {
              fromDetail: true,
              selectedTab: tab,
              returnPage: fromPage,
            },
          });
        }}
      />
      <Box spacingInset="none" minHeight={557}>
          {/* Summary list button */}
          <Box spacingInset="500 none 500 none">
            <SummaryListButton onSummaryListInClient={onSummaryListInClient} />
          </Box>

          {/*Scroll bar section*/}
          <Box
            height="75vh"
            spacingInset="none none none 500"
            overflowY="scroll"
          >
              {/* Show Meeting Participants view */}
              {showParticipants ? (
                <Column spacing="none" spacingInset="none 450 none none">
                  <MeetingParticipants
                    attendees={summary.attendees}
                    onBack={() => setShowParticipants(false)}
                  />
                </Column>
              ) : (
                <Column spacing="400" spacingInset="none 450 none none">
                  {/* Summary title */}
                  <Text type="h200">
                    {summary.meetingTitle}
                  </Text>

                  {/* Summary time with calendar icon */}
                  <Row spacing="200" alignmentVertical="center">
                    <Icon tokens={calendarTokens} color="secondary" />
                    <Text type="b100" color="secondary" className="summary-time">
                      {summary.meetingDateAndTime}
                    </Text>
                  </Row>

                  {/* Attendee list */}
                  <AttendeeListInDetailPage
                    attendeeNameList={summary.attendeeNameList}
                    attendees={summary.attendees}
                    isAttendanceReportEnabled={isAttendanceReportEnabled}
                    onViewParticipants={() => setShowParticipants(true)}
                  />

                  <Divider spacingBefore="0" spacingAfter="0" />

                  {summary.summaryDetailLevel && summary.summaryDetailLevel !== VERBOSITY_LEVELS.CONCISE && (
                    <img
                      src={summary.summaryDetailLevel === VERBOSITY_LEVELS.MODERATE ? badgeModerate : badgeDetailed}
                      alt={summary.summaryDetailLevel}
                      width={90}
                      height={20}
                    />
                  )}

                  <SummaryBodyInDetailPage summary={summary} isTopicNotApproved={summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.NOT_APPROVED}/>
                </Column>
              )}
          </Box>

          {/* Feedback */}
          {!showParticipants && (!summary.feedback && !summary.feedbackComment) && (
            summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.APPROVED ? (
              <SummaryFeedback aimsUuid={id} />
            ) : summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.NOT_APPROVED ? (
              <SummaryNotApprovedFeedback aimsUuid={id} />
            ) : null
          )}
      </Box>
    </Row>
  );
};

export default SummaryDetailInClient;
